/* eslint-disable react/display-name */
import clsx from "clsx";
import React, { FunctionComponent, memo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { HashLink } from "../../navigation/HashLink";
import { useAuth } from "../../stores/hooks/useAuth";
import { useDealerEnableCustomerLogin } from "../../stores/hooks/useDealerEnableCustomerLogin";
import { HashtagString, useHashState } from "../../stores/useHashState";
import { Button, PrimaryButton } from "../../ui/Button/Button";
import { Dialog, DialogProps } from "../../ui/Dialog/Dialog";
import { DialogHeader } from "../../ui/Dialog/DialogHeader";
import { Input } from "../../ui/Input/Input";
import { useForm } from "../../util/useForm";
import { passwordResetHash } from "./PasswordResetDialog";
import { signUpHash } from "./SignupDialog";

type SignInDialogProps = Omit<DialogProps, "children" | "visible">;

const schema = z.object({
  password: z.string().nonempty({ message: "form.error.password" }),
  username: z.string().email({ message: "form.error.username" }),
});

export const signInHash: HashtagString = "#sign-in";

const Uncached: FunctionComponent<SignInDialogProps> = ({
  className,
  ...props
}) => {
  const { t } = useTranslation();
  const [, { isSigningIn, signIn }] = useAuth();
  const [visible, { hide }] = useHashState(signInHash);
  const dealerEnableCustomerLogin = useDealerEnableCustomerLogin();

  const [handleForm] = useForm({
    onSubmit(data) {
      signIn({
        password: data.password,
        rememberMe: true,
        username: data.username,
      }).then((success) => {
        if (success) {
          hide();
        }
      });
    },
    schema,
  });

  return (
    <Dialog
      {...props}
      className={clsx(
        className,
        "md:p-4 lg:rounded-panel md:w-full max-w-[29rem]"
      )}
      onClose={hide}
      visible={visible}
    >
      <DialogHeader
        subtitle={
          dealerEnableCustomerLogin && (
            <HashLink className="underline" hash={signUpHash}>
              {t("auth.lickToRegister")}
            </HashLink>
          )
        }
        headingClassName="text-xl md:text-3xl font-medium"
        onClose={hide}
        title={t("auth.signInTitle")}
      />
      <form className="grid gap-y-4 px-4 mt-4" noValidate onSubmit={handleForm}>
        <fieldset className="grid gap-2">
          <label
            className="text-xs text-ink-2 font-thin"
            htmlFor="sign-in-username"
          >
            {t("form.label.username")}
          </label>
          <Input
            aria-label="username"
            autoCapitalize="off"
            autoComplete="username"
            autoCorrect="off"
            id="sign-in-username"
            name="username"
            placeholder={t("form.placeholder.username")}
            type="email"
          />
        </fieldset>
        <fieldset className="grid gap-2">
          <div className="flex justify-between items-center text-xs">
            <label className="text-ink-2 font-thin" htmlFor="sign-in-password">
              {t("form.label.password")}
            </label>
            <span>
              (
              <HashLink hash={passwordResetHash}>
                <a className="underline">{t("auth.forgotPassword")}</a>
              </HashLink>
              )
            </span>
          </div>
          <Input
            aria-label="yourPassword"
            autoCapitalize="off"
            autoComplete="current-password"
            autoCorrect="off"
            id="sign-in-password"
            name="password"
            placeholder={t("form.placeholder.password")}
            type="password"
          />
        </fieldset>
        <div className="grid grid-cols-2 gap-2 pb-6 mt-2">
          <Button
            aria-label="closeSignInDialog"
            className="text-ink-2 underline font-thin h-14"
            onClick={hide}
            type="button"
          >
            {t("auth.cancel")}
          </Button>
          <PrimaryButton
            aria-label="submitSignInForm"
            busy={isSigningIn}
            id="sign-in-submit"
            type="submit"
          >
            {t("auth.signIn")}
          </PrimaryButton>
        </div>
      </form>
    </Dialog>
  );
};

export const SignInDialog = memo(Uncached);
